import { toast } from 'react-toastify';

/**
 * Standard API Response Structure
 */
export interface ApiResponse<T = any> {
  success: boolean;
  severity: 'success' | 'info' | 'warning' | 'error';
  message: string;
  data?: T;
  errors?: string[];
}

/**
 * Toast notification configuration
 */
const toastConfig = {
  position: 'top-right' as const,
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

/**
 * Shows a toast notification based on API response severity
 * @param response - The API response object
 * @returns The data from the response
 */
export function handleApiResponse<T = any>(response: ApiResponse<T>): T | null {
  const { success, severity, message, data, errors } = response;

  // Show appropriate toast based on severity
  switch (severity) {
    case 'success':
      toast.success(message, toastConfig);
      break;
    case 'info':
      toast.info(message, toastConfig);
      break;
    case 'warning':
      toast.warning(message, toastConfig);
      break;
    case 'error':
      toast.error(message, toastConfig);
      // Show additional error details if available
      if (errors && errors.length > 0) {
        errors.forEach((error, index) => {
          setTimeout(() => toast.error(error, { ...toastConfig, autoClose: 7000 }), index * 100);
        });
      }
      break;
    default:
      // Fallback for unknown severity
      if (success) {
        toast.success(message, toastConfig);
      } else {
        toast.error(message, toastConfig);
      }
  }

  return data ?? null;
}

/**
 * Handles API errors and shows appropriate toast notifications
 * @param error - The error object from axios or other sources
 * @param fallbackMessage - Fallback message if error doesn't have a message
 */
export function handleApiError(error: any, fallbackMessage: string = 'Ocorreu um erro inesperado'): void {
  // Check if error is already a standardized ApiResponse (from axios interceptor)
  if (error && typeof error === 'object' && 'severity' in error && 'message' in error) {
    handleApiResponse(error as ApiResponse);
    return;
  }

  // Check if error is wrapped in response.data (non-intercepted axios errors)
  if (error?.response?.data) {
    const responseData = error.response.data;

    // Check if it's a standardized ApiResponse
    if (typeof responseData === 'object' && 'severity' in responseData && 'message' in responseData) {
      handleApiResponse(responseData as ApiResponse);
      return;
    }

    // Handle legacy format { message: "..." }
    if (responseData.message) {
      toast.error(responseData.message, toastConfig);
      return;
    }
  }

  // Handle plain Error objects
  if (error instanceof Error) {
    toast.error(error.message || fallbackMessage, toastConfig);
    return;
  }

  // Handle string errors
  if (typeof error === 'string') {
    toast.error(error, toastConfig);
    return;
  }

  // Fallback
  toast.error(fallbackMessage, toastConfig);
}

/**
 * Utility functions for creating toast notifications manually
 */
export const notify = {
  success: (message: string) => toast.success(message, toastConfig),
  info: (message: string) => toast.info(message, toastConfig),
  warning: (message: string) => toast.warning(message, toastConfig),
  error: (message: string) => toast.error(message, toastConfig),
};
